import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Monitor, FolderOpen, BadgeDollarSign, Mail, Home } from "lucide-react";

export interface TaskbarWindow {
  id: string;
  title: string;
  active: boolean;
  onClick: () => void;
}

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const label = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return (
    <div data-testid="taskbar-clock" className="bevel-in flex h-8 shrink-0 items-center bg-white px-2 font-spacemono text-[11px] font-bold">
      {label}
    </div>
  );
};

const START_LINKS = [
  { to: "/", label: "home", icon: <Home size={14} /> },
  { to: "/portfolio", label: "portfolio", icon: <FolderOpen size={14} /> },
  { to: "/rates", label: "rate_card", icon: <BadgeDollarSign size={14} /> },
  { to: "/inquire", label: "new_project", icon: <Mail size={14} /> },
];

export const Taskbar = ({ windows = [] }: { windows?: TaskbarWindow[] }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();

  return (
    <div data-testid="taskbar" className="fixed inset-x-0 bottom-0 z-[90] flex h-12 items-center gap-2 border-t-[3px] border-ink bg-cream px-2">
      <div className="relative shrink-0">
        <button
          data-testid="start-button"
          onClick={() => setMenuOpen((o) => !o)}
          className="btn-chunky h-8 bg-lnpink px-2.5 text-[11px]"
          aria-expanded={menuOpen}
        >
          <Monitor size={14} /> <span className="hidden xs:inline sm:inline">START</span>
        </button>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              data-testid="start-menu"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8, transition: { duration: 0.12 } }}
              className="os-window absolute bottom-11 left-0 z-50 w-52"
            >
              <div className="os-titlebar">
                <span>LATE NIGHTS OS</span>
                <button aria-label="Close start menu" data-testid="start-menu-close" className="os-ctrl" onClick={() => setMenuOpen(false)}>×</button>
              </div>
              <div className="flex flex-col gap-0.5 bg-white p-1.5">
                {START_LINKS.map((l) => (
                  <button
                    key={l.to}
                    data-testid={`start-link-${l.label}`}
                    onClick={() => { setMenuOpen(false); nav(l.to); }}
                    className="flex items-center gap-2 border-2 border-transparent px-2 py-2 text-left font-spacemono text-xs font-bold hover:border-ink hover:bg-lavender"
                  >
                    {l.icon} {l.label}.exe
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex flex-1 items-center gap-1.5 overflow-x-auto">
        {windows.map((w) => (
          <button
            key={w.id}
            data-testid={`taskbar-window-${w.id}`}
            onClick={w.onClick}
            className={`h-8 whitespace-nowrap border-2 border-ink px-2.5 font-spacemono text-[10px] font-bold ${
              w.active ? "translate-x-[1px] translate-y-[1px] bg-butter" : "bg-white shadow-hard-sm"
            }`}
          >
            {w.title}
          </button>
        ))}
      </div>

      <nav data-testid="taskbar-nav" className="hidden shrink-0 items-center gap-1.5 font-spacemono text-[10px] font-bold sm:flex">
        <button data-testid="nav-home" onClick={() => nav("/")} className="hover:bg-butter px-1 py-1">home</button>/
        <button data-testid="nav-portfolio" onClick={() => nav("/portfolio")} className="hover:bg-butter px-1 py-1">work</button>/
        <button data-testid="nav-rates" onClick={() => nav("/rates")} className="hover:bg-butter px-1 py-1">rates</button>/
        <button data-testid="nav-inbox" onClick={() => nav("/inquire")} className="hover:bg-butter px-1 py-1">inbox</button>
      </nav>

      <Clock />
    </div>
  );
};
